<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="Microsoft Peering向けNAPT設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer ref="observer" v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <!--VPN/VNNコード-->
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <app-label-group
                      label="VPN/VNコード"
                      :value="naptSetting.vpnVnCode"
                    />
                  </v-col>
                </v-row>
                <!--NAPT用グローバルIP-->
                <v-row>
                  <v-col cols="12">
                    <app-label-group label="NAPT用グローバルIP" />
                    <div class="ml-3">
                      現在 {{ naptSetting.usedNapt }}個 / 上限
                      {{ naptSetting.upperLimitNapt }}個
                    </div>
                    <div class="ml-3">
                      追加グローバルIP数 : {{ addIpCount }} 個
                    </div>
                    <app-label-group label="グローバルIP" class="ml-3">
                      <template #value>
                        <v-row
                          v-for="(item, index) in msNaptGlobalIpList"
                          :key="index"
                          align="center"
                          class="ml-3"
                        >
                          <v-col :cols="item.isInitialAddress ? 7 : 5">
                            {{ item.globalIpAddress }}
                            <span v-if="item.isInitialAddress">
                              (デフォルト)</span
                            >
                          </v-col>
                          <v-col cols="2">
                            <v-btn
                              icon="$mdiMinus"
                              v-if="!item.isInitialAddress"
                              size="small"
                              variant="outlined"
                              class="rounded-sm"
                              @click="removeNapt(index)"
                            >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </app-label-group>

                    <div v-for="i in addIpCount" :key="i">
                      <v-row align="center" class="ml-6 mt-2">
                        <v-col cols="5"> 設定完了後にIPが払い出されます </v-col>
                        <v-col cols="2">
                          <v-btn
                            icon="$mdiMinus"
                            v-if="canRemove"
                            size="small"
                            variant="outlined"
                            class="rounded-sm"
                            @click="addIpCount--"
                          >
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>

                    <v-row align="center" class="ml-6 mt-2">
                      <v-col cols="5"></v-col>
                      <v-col cols="2">
                        <v-btn
                          :disabled="!canAdd"
                          icon="$mdiPlus"
                          size="small"
                          variant="outlined"
                          @click="addIpCount++"
                          class="rounded-sm"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">設定</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type {
  NaptGlobalIpEntity,
  NatNaptDetailEntity,
} from "@/apis/MSPeeringIpApi";
import cloneDeep from "lodash/cloneDeep";
import MSNaptSettingModifyConfirm from "@/modals/msNaptSetting/MSNaptSettingModifyConfirm.vue";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "MSNaptSettingModify",
  props: {
    /** 設定変更時、使用する排他キー */
    keyId: {
      type: String as PropType<string>,
      required: true,
    },
    /** NAPT用グローバルIP設定情報 */
    naptSetting: {
      type: Object as PropType<NatNaptDetailEntity>,
      required: true,
    },
  },
  data() {
    return {
      msNaptGlobalIpList: cloneDeep(this.naptSetting.msNaptGlobalIpList),
      deletedIp: [] as NaptGlobalIpEntity[],
      /** 追加グローバルIP数の初期値設定 */
      addIpCount: this.naptSetting.usedNapt === 0 ? 1 : 0,
    };
  },
  computed: {
    /** true: 削除可能, false: 削除不可 */
    canRemove(): boolean {
      return this.msNaptGlobalIpList.length + this.addIpCount > 1;
    },
    /** true: 追加可能, false: 追加不可 */
    canAdd(): boolean {
      return (
        this.naptSetting.msNaptGlobalIpList.length + this.addIpCount <
        this.naptSetting.upperLimitNapt
      );
    },
  },
  methods: {
    /** NAPTグローバルIP削除処理 */
    removeNapt(index: number) {
      const deleted = this.msNaptGlobalIpList.splice(index, 1);
      this.deletedIp = [...this.deletedIp, ...deleted];
    },

    async submit() {
      /** 確認画面表示 */
      await this.$modal.show(MSNaptSettingModifyConfirm, {
        addNapt: this.addIpCount,
        naptSetting: this.naptSetting,
        msNaptGlobalIpList: this.msNaptGlobalIpList,
      });

      /** グローバルIP更新API呼び出し処理 */
      await this.$api.msPeeringIp.updateGlobalIp(
        this.naptSetting.msServiceSeq,
        {
          addNapt: this.addIpCount,
          addNat: null,
          msNatAddPrivateIpList: null,
          deleteMsNaptGlobalIpList: this.deletedIp.map(
            (e) => e.cloudGlobalIpAddressSeq
          ),
          deleteMsNatGlobalIpList: null,
          msPeeringIpKeyId: this.keyId,
        }
      );
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
